/**
 * LoginPage — Training Portal authentication screen.
 *
 * Route: /app/login (public)
 *
 * Acceptance criteria (from ADR-007 + api-contracts.md):
 * - AC-1: POST /api/v1/auth/login with email + password
 * - AC-2: 200 OK + role is `admin` only (no training rights) →
 *         show "no training access" error (do NOT store token)
 * - AC-3: 200 OK + role is `agent` or `manager` → write sessionStorage,
 *         redirect /scenarios
 * - AC-4: 401 INVALID_CREDENTIALS → show inline error
 * - AC-5: 423 ACCOUNT_LOCKED → show lockout message
 * - AC-6: Network error → show network error message
 * - AC-7: All text internationalised via react-i18next namespace `training`
 * - AC-8: Submit disabled while in-flight; aria-busy on button
 * - AC-9: Password field type="password"
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ApiResponse, LoginRequest, LoginResponseData } from '../types';
import { LanguageSelector } from '../components/LanguageSelector';
import styles from './LoginPage.module.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Roles that are permitted to access the Training Portal (ADR-007) */
// Portal access is decided by the backend (`user.portal`, derived from role via
// ADR-011 B-7). Don't re-derive it from the role string — that set drifts
// (e.g. `learner` maps to training but was missing here).
const TRAINING_ALLOWED_PORTALS = new Set(['training']);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoginError =
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_LOCKED'
  | 'FORBIDDEN'
  | 'NETWORK_ERROR'
  | null;

// ---------------------------------------------------------------------------
// API helper — does NOT use the shared apiClient because login has no Bearer token
// ---------------------------------------------------------------------------

async function postLogin(payload: LoginRequest): Promise<ApiResponse<LoginResponseData>> {
  const res = await fetch('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as ApiResponse<LoginResponseData>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LoginPage(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<LoginError>(null);

  // -------------------------------------------------------------------------
  // Submit handler
  // -------------------------------------------------------------------------

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (isSubmitting) return;

      setLoginError(null);
      setIsSubmitting(true);

      try {
        const body = await postLogin({ email, password });

        if (body.status === 'error') {
          const code = body.error?.code ?? '';
          if (code === 'INVALID_CREDENTIALS') setLoginError('INVALID_CREDENTIALS');
          else if (code === 'ACCOUNT_LOCKED') setLoginError('ACCOUNT_LOCKED');
          else setLoginError('INVALID_CREDENTIALS');
          return;
        }

        // status === 'success'
        const data = body.data;
        if (!data) {
          setLoginError('INVALID_CREDENTIALS');
          return;
        }

        // AC-2: admin-only user tried to use the Training Portal
        if (!TRAINING_ALLOWED_PORTALS.has(data.user.portal)) {
          setLoginError('FORBIDDEN');
          return;
        }

        // AC-3: authorised — persist to sessionStorage (NOT localStorage per CLAUDE.md)
        sessionStorage.setItem('access_token', data.access_token);
        sessionStorage.setItem('refresh_token', data.refresh_token);
        sessionStorage.setItem('user_role', data.user.role);
        sessionStorage.setItem('user_language', data.user.language);
        sessionStorage.setItem('user_profile', JSON.stringify(data.user));

        // Redirect to dashboard/home (landing page) after login
        navigate('/home', { replace: true });
      } catch (err: unknown) {
        // Network error (fetch threw) or JSON parse failure
        if (err instanceof TypeError) {
          setLoginError('NETWORK_ERROR');
        } else {
          setLoginError('NETWORK_ERROR');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, isSubmitting, navigate],
  );

  // -------------------------------------------------------------------------
  // Error message lookup
  // -------------------------------------------------------------------------

  function errorMessage(): string | null {
    switch (loginError) {
      case 'INVALID_CREDENTIALS':
        return t('login.errors.invalidCredentials');
      case 'ACCOUNT_LOCKED':
        return t('login.errors.accountLocked');
      case 'FORBIDDEN':
        return t('login.errors.forbidden');
      case 'NETWORK_ERROR':
        return t('login.errors.networkError');
      default:
        return null;
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const errMsg = errorMessage();

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Language picker — top-right of card, before any auth context */}
        <LanguageSelector />

        {/* Logotype */}
        <div className={styles.header}>
          <div className={styles.logoMark} aria-hidden="true">
            <span className={styles.logoText}>ARIA</span>
          </div>
          <p className={styles.tagline}>{t('brand.tagline')}</p>
          <h1 className={styles.title}>{t('login.title')}</h1>
          <p className={styles.subtitle}>{t('login.subtitle')}</p>
        </div>

        {/* Error banner */}
        {errMsg !== null && (
          <div className={styles.errorBanner} role="alert">
            {errMsg}
          </div>
        )}

        {/* Login form */}
        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
          {/* Email */}
          <div className={styles.fieldGroup}>
            <label htmlFor="login-email" className={styles.label}>
              {t('login.emailLabel')}
            </label>
            <input
              id="login-email"
              type="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              autoComplete="email"
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          {/* Password — AC-9: type="password" */}
          <div className={styles.fieldGroup}>
            <label htmlFor="login-password" className={styles.label}>
              {t('login.passwordLabel')}
            </label>
            <input
              id="login-password"
              type="password"
              className={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={isSubmitting}
            />
          </div>

          {/* AC-8: Submit disabled while in-flight */}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || email.trim() === '' || password === ''}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? t('login.submitting') : t('login.submitButton')}
          </button>
        </form>

        {/* Forgot password */}
        <a href="/app/forgot-password" className={styles.forgotLink}>
          {t('login.forgotPassword')}
        </a>
      </div>
    </div>
  );
}
